import { ScrollArea } from '@mantine/core';
import { getQueryValue, parseMaybeEnumValue } from '@zorro/common-utils';
import { Modal } from '@zorro/design-system/v2/Modal';
import { MODAL_ZINDEX } from '@zorro/types';
import { Box, Stack, Text } from '@zorro/web/design-system';
import { useAnalytics } from '@zorro/web/shared-ui';
import { FlowIdProvider, useAppRouter } from '@zorro/web/ui-utils';
import { useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { $enum } from 'ts-enum-util';

import { CarrierApplicationModalStepType, getEmployeeTabRoute } from '.';
import { EmployeePageTabType } from '../../EmployeePage/EmployeePageContent';
import { useEnrollmentAllowanceStore } from '../../EnrollmentAllowanceCard';
import { ModalFooter } from '../../ModalComponents';
import {
  CarrierApplicationCompleteStep,
  useCarrierApplicationForm,
} from '../CarrierApplicationCompleteStep';
import { CarrierApplicationEnrollmentInfoStep } from '../CarrierApplicationEnrollmentInfoStep';
import { CarrierApplicationPaymentMethodStep } from '../CarrierApplicationPaymentMethodStep';
import { CarrierApplicationReviewView } from '../CarrierApplicationReviewView';

type Props = {
  onboardingPeriodId: string;
  employerId: string;
  employeeId: string;
  onClose: () => void;
  isOpen: boolean;
  tab: EmployeePageTabType;
};

const CarrierApplicationModalContent = ({
  onboardingPeriodId,
  employerId,
  employeeId,
  onClose,
  isOpen,
  tab,
}: Props) => {
  const { router, navigateToRoute } = useAppRouter();
  const { sendEvent } = useAnalytics();
  const setIsDirty = useEnrollmentAllowanceStore((state) => state.setIsDirty);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [isQuitConfirmOpen, setIsQuitConfirmOpen] = useState(false);

  const step =
    parseMaybeEnumValue(
      CarrierApplicationModalStepType,
      getQueryValue(router.query.step)
    ) ?? CarrierApplicationModalStepType.ENROLLMENT_INFO;
  const benefitId = getQueryValue(router.query.benefitId);

  // Owned here (not by the step) so the form and the EnrollConnect result
  // survive step navigation - e.g. the review-details round-trip.
  const carrierApplicationForm = useCarrierApplicationForm({
    employerId,
    employeeId,
    onboardingPeriodId,
    benefitId,
  });
  const submissionMethod = useWatch({
    control: carrierApplicationForm.submissionForm.control,
    name: 'submissionMethod',
  });

  const handleScrollToTop = () =>
    viewportRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

  const handleClose = () => {
    setIsDirty(false);
    onClose();
    navigateToRoute(
      getEmployeeTabRoute(tab),
      [{ employerId, employeeId, onboardingPeriodId }],
      { step: CarrierApplicationModalStepType.ENROLLMENT_INFO },
      false,
      false
    );
  };

  const handleDismiss = () => {
    if (
      step === CarrierApplicationModalStepType.COMPLETE_APPLICATION &&
      !carrierApplicationForm.isEditMode
    ) {
      setIsQuitConfirmOpen(true);
      return;
    }
    handleClose();
  };

  const handleQuit = () => {
    setIsQuitConfirmOpen(false);
    void sendEvent('exit_application_flow_incomplete', {
      onboarding_period_id: onboardingPeriodId,
      method: submissionMethod ?? undefined,
    });
    handleClose();
  };

  return (
    <>
      <Modal
        onClose={() => setIsQuitConfirmOpen(false)}
        isOpen={isQuitConfirmOpen}
        title="Quit before confirming application sent?"
        size="md"
        zIndex={MODAL_ZINDEX + 1}
        footer={
          <ModalFooter
            submitText="Quit"
            submitVariant="negative"
            onClose={() => setIsQuitConfirmOpen(false)}
            onSubmit={handleQuit}
            secondaryText="Keep editing"
          />
        }
      >
        <Stack>
          <Text size="sm">
            Quitting now won't update the status to 'Carrier application sent' —
            even if you already submitted it to the carrier.
          </Text>
        </Stack>
      </Modal>
      <Modal
        onClose={handleDismiss}
        isOpen={isOpen}
        size="83rem"
        shouldCloseOnClickOutside={false}
        shouldCloseOnEscape={false}
      >
        <ScrollArea
          styles={{ viewport: { overflowX: 'hidden' } }}
          viewportRef={viewportRef}
          h="calc(100vh - 14rem)"
          offsetScrollbars="y"
          type="never"
        >
          {$enum.mapValue(step).with({
            [CarrierApplicationModalStepType.ENROLLMENT_INFO]: (
              <CarrierApplicationEnrollmentInfoStep
                employerId={employerId}
                employeeId={employeeId}
                onboardingPeriodId={onboardingPeriodId}
                benefitId={benefitId}
                onSubmit={handleScrollToTop}
                tab={tab}
              />
            ),
            [CarrierApplicationModalStepType.PAYMENT_METHOD]: (
              <CarrierApplicationPaymentMethodStep
                employerId={employerId}
                employeeId={employeeId}
                onboardingPeriodId={onboardingPeriodId}
                benefitId={benefitId}
                onSubmit={handleScrollToTop}
                tab={tab}
              />
            ),
            [CarrierApplicationModalStepType.COMPLETE_APPLICATION]: (
              <CarrierApplicationCompleteStep
                employerId={employerId}
                employeeId={employeeId}
                onboardingPeriodId={onboardingPeriodId}
                benefitId={benefitId}
                onClose={handleClose}
                onDetailsReviewOpen={handleScrollToTop}
                carrierApplicationForm={carrierApplicationForm}
                tab={tab}
              />
            ),
            [CarrierApplicationModalStepType.REVIEW]: (
              <CarrierApplicationReviewView
                employerId={employerId}
                employeeId={employeeId}
                onboardingPeriodId={onboardingPeriodId}
                benefitId={benefitId}
              />
            ),
          })}
        </ScrollArea>
        <Box id="application-modal-footer" m="0 -2.5rem -1rem -1.75rem" />
      </Modal>
    </>
  );
};

export const CarrierApplicationModal = ({ isOpen, ...props }: Props) => (
  <FlowIdProvider isOpen={isOpen}>
    <CarrierApplicationModalContent isOpen={isOpen} {...props} />
  </FlowIdProvider>
);
